import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {{ tabs: Element, tabBtns: Element[], body: Element, categories: Element[] }} Refs
 */

/** @extends {Component<Refs>} */
class AdFaqComponent extends Component {
    /** @type {IntersectionObserver | null} */
    #observer = null;

    /** @type {boolean} */
    #scrollingToCategory = false;

    /** @type {ReturnType<typeof setTimeout> | null} */
    #scrollTimeout = null;

    /** @type {AbortController | null} */
    #accordionController = null;

    /** @type {Map<HTMLDetailsElement, AbortController>} */
    #pendingClose = new Map();

    connectedCallback() {
        super.connectedCallback();
        this.#initAccordions();
        this.#initScrollSpy();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#observer?.disconnect();
        this.#accordionController?.abort();
        this.#clearAllPendingCloses();
        if (this.#scrollTimeout) clearTimeout(this.#scrollTimeout);
    }

    updatedCallback() {
        this.#observer?.disconnect();
        this.#accordionController?.abort();
        this.#clearAllPendingCloses();
        this.#initAccordions();
        this.#initScrollSpy();
    }

    #initAccordions() {
        this.#accordionController = new AbortController();
        const { signal } = this.#accordionController;

        this.querySelectorAll('.na-faq__details').forEach((node) => {
            const details = /** @type {HTMLDetailsElement} */ (node);
            const summary = details.querySelector('summary');
            if (!summary) return;

            summary.addEventListener(
                'click',
                (event) => {
                    event.preventDefault();

                    if (details.hasAttribute('data-open')) {
                        this.#closeDetails(details);
                    } else {
                        this.#openDetails(details);
                    }
                },
                { signal }
            );
        });
    }

    /**
     * @param {HTMLDetailsElement} details
     */
    #openDetails(details) {
        this.#clearPendingClose(details);
        details.open = true;
        details.getBoundingClientRect();
        details.setAttribute('data-open', '');
    }

    /**
     * @param {HTMLDetailsElement} details
     */
    #closeDetails(details) {
        details.removeAttribute('data-open');
        this.#clearPendingClose(details);

        if (prefersReducedMotion()) {
            details.open = false;
            return;
        }

        const controller = new AbortController();
        this.#pendingClose.set(details, controller);

        details.addEventListener(
            'transitionend',
            () => {
                this.#pendingClose.delete(details);
                details.open = false;
            },
            { once: true, signal: controller.signal }
        );
    }

    /**
     * @param {HTMLDetailsElement} details
     */
    #clearPendingClose(details) {
        this.#pendingClose.get(details)?.abort();
        this.#pendingClose.delete(details);
    }

    #clearAllPendingCloses() {
        for (const controller of this.#pendingClose.values()) {
            controller.abort();
        }
        this.#pendingClose.clear();
    }

    /**
     * @param {Event} event
     */
    handleTabClick(event) {
        const btn = /** @type {HTMLButtonElement} */ (event.target);
        const targetId = btn.dataset.tabTarget;
        if (!targetId) return;

        const target = document.getElementById(targetId);
        if (!target) return;

        this.#scrollingToCategory = true;
        this.#setActiveTab(btn);

        target.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });

        if (this.#scrollTimeout) clearTimeout(this.#scrollTimeout);
        this.#scrollTimeout = setTimeout(() => {
            this.#scrollingToCategory = false;
        }, 800);
    }

    /**
     * @param {HTMLButtonElement} activeBtn
     */
    #setActiveTab(activeBtn) {
        const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('[data-tab-target]'));

        buttons.forEach((btn) => {
            const isActive = btn === activeBtn;
            btn.setAttribute('aria-current', isActive ? 'true' : 'false');
            btn.classList.toggle('button', isActive);
            btn.classList.toggle('button-secondary', !isActive);
        });
    }

    #initScrollSpy() {
        const categories = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-faq-category]'));

        if (!categories.length) return;

        const headerHeight = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '100',
            10
        );

        this.#observer = new IntersectionObserver(
            (entries) => {
                if (this.#scrollingToCategory) return;

                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;

                    const categoryId = entry.target.id;
                    const matchingBtn = /** @type {HTMLButtonElement | null} */ (
                        this.querySelector(`[data-tab-target="${categoryId}"]`)
                    );

                    if (matchingBtn) this.#setActiveTab(matchingBtn);
                });
            },
            {
                rootMargin: `-${headerHeight + 16}px 0px -60% 0px`,
                threshold: 0,
            }
        );

        categories.forEach((cat) => this.#observer?.observe(cat));
    }
}

if (!customElements.get('na-faq-component')) {
    customElements.define('na-faq-component', AdFaqComponent);
}
